import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { IMasterTemplate } from '../../interfaces';

/**
 * Servicio de vista para gestión del formulario de plantillas
 * Maneja el estado del FormGroup y validaciones
 */
@Injectable({
  providedIn: 'root'
})
export class TemplateService {
  entity!: FormGroup;
  isEditMode = false;
  activeChannels: string[] = ['email']; // Email siempre activo por defecto
  selectedTemplate: IMasterTemplate | null = null;

  constructor(private fb: FormBuilder) {}

  /**
   * Inicializa un formulario vacío para crear nueva plantilla
   */
  newEntity() {
    this.isEditMode = false;
    this.selectedTemplate = null;
    this.activeChannels = ['email'];

    this.entity = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(200)]],
      description: ['', [Validators.maxLength(500)]],
      
      // Email Template (REQUERIDO)
      emailTemplate: this.fb.group({
        subject: ['', [Validators.required, Validators.maxLength(200)]],
        html: ['', [Validators.required]]
      }),
      
      // SMS Template (OPCIONAL)
      smsTemplate: this.fb.group({
        message: ['', [Validators.maxLength(160)]]
      }),
      
      // WhatsApp Template (OPCIONAL)
      whatsappTemplate: this.fb.group({
        message: ['']
      }),
      
      // Call Script (OPCIONAL) - Guión para llamadas manuales
      callScript: this.fb.group({
        greeting: [''],
        mainMessage: [''],
        closing: ['']
      }),

      // Auto Call Config (OPCIONAL) - Configuración de llamada automatizada
      autoCallConfig: this.fb.group({
        callType: ['TTS'],
        ttsMessage: ['', [Validators.maxLength(2000)]],
        audioUrl: [''],
        audioFileName: ['']
      }),
      
      variables: [['clientName', 'leaseNumber', 'amount', 'dueDate'], [Validators.required]],
      active: [true]
    });
  }

  /**
   * Carga una plantilla existente en el formulario para edición
   * @param template - Plantilla a editar
   */
  loadEntityForEdit(template: IMasterTemplate) {
    this.isEditMode = true;
    this.selectedTemplate = template;
    
    // Determinar canales activos basados en qué templates están configurados
    this.activeChannels = ['email']; // Email siempre activo
    
    // Verificar si existen otros templates
    if (template.smsTemplate && template.smsTemplate.message) {
      this.activeChannels.push('sms');
    }
    if (template.whatsappTemplate && template.whatsappTemplate.message) {
      this.activeChannels.push('whatsapp');
    }
    if (template.callScript && template.callScript.greeting) {
      this.activeChannels.push('call');
    }
    if (template.autoCallConfig && (template.autoCallConfig.ttsMessage || template.autoCallConfig.audioUrl)) {
      this.activeChannels.push('autoCall');
    }
    
    // Inicializar el formulario si no existe
    if (!this.entity) {
      this.entity = this.fb.group({
        name: ['', [Validators.required, Validators.maxLength(200)]],
        description: ['', [Validators.maxLength(500)]],
        
        emailTemplate: this.fb.group({
          subject: ['', [Validators.required, Validators.maxLength(200)]],
          html: ['', [Validators.required]]
        }),
        
        smsTemplate: this.fb.group({
          message: ['', [Validators.maxLength(160)]]
        }),
        
        whatsappTemplate: this.fb.group({
          message: ['']
        }),
        
        callScript: this.fb.group({
          greeting: [''],
          mainMessage: [''],
          closing: ['']
        }),

        autoCallConfig: this.fb.group({
          callType: ['TTS'],
          ttsMessage: ['', [Validators.maxLength(2000)]],
          audioUrl: [''],
          audioFileName: ['']
        }),
        
        variables: [[], [Validators.required]],
        active: [true]
      });
    }
    
    // Cargar datos en el formulario
    this.entity.patchValue({
      name: template.name,
      description: template.description || '',
      emailTemplate: template.emailTemplate,
      smsTemplate: template.smsTemplate || { message: '' },
      whatsappTemplate: template.whatsappTemplate || { message: '' },
      callScript: template.callScript || { greeting: '', mainMessage: '', closing: '' },
      autoCallConfig: template.autoCallConfig || { callType: 'TTS', ttsMessage: '', audioUrl: '', audioFileName: '' },
      variables: template.variables,
      active: template.active
    });
  }

  /**
   * Extrae variables de un texto en formato {{variable}}
   * @param text - Texto que contiene variables
   * @returns Array de nombres de variables encontradas
   */
  extractVariables(text: string): string[] {
    if (!text) return [];
    
    const regex = /\{\{(\w+)\}\}/g;
    const matches = text.matchAll(regex);
    const variables = new Set<string>();
    
    for (const match of matches) {
      variables.add(match[1]);
    }
    
    return Array.from(variables);
  }

  /**
   * Extrae todas las variables de todos los templates configurados
   * @returns Array con todas las variables únicas encontradas
   */
  extractAllVariables(): string[] {
    const allText: string[] = [];
    
    // Email template (siempre existe)
    const emailTemplate = this.entity.get('emailTemplate');
    if (emailTemplate) {
      allText.push(emailTemplate.get('subject')?.value || '');
      allText.push(emailTemplate.get('html')?.value || '');
      allText.push(emailTemplate.get('text')?.value || '');
    }
    
    // SMS template (opcional)
    const smsTemplate = this.entity.get('smsTemplate');
    if (smsTemplate) {
      allText.push(smsTemplate.get('message')?.value || '');
    }
    
    // WhatsApp template (opcional)
    const whatsappTemplate = this.entity.get('whatsappTemplate');
    if (whatsappTemplate) {
      allText.push(whatsappTemplate.get('message')?.value || '');
    }
    
    // Call script (opcional)
    const callScript = this.entity.get('callScript');
    if (callScript) {
      allText.push(callScript.get('greeting')?.value || '');
      allText.push(callScript.get('mainMessage')?.value || '');
      allText.push(callScript.get('closing')?.value || '');
    }

    // Auto call config (opcional) - TTS message puede tener variables
    const autoCallConfig = this.entity.get('autoCallConfig');
    if (autoCallConfig) {
      allText.push(autoCallConfig.get('ttsMessage')?.value || '');
    }
    
    // Extraer variables de todo el texto
    const combinedText = allText.join(' ');
    return this.extractVariables(combinedText);
  }

  /**
   * Actualiza el array de variables del formulario extrayendo las variables usadas
   */
  updateVariables() {
    const variables = this.extractAllVariables();
    this.entity.get('variables')?.setValue(variables);
  }

  /**
   * Valida que todas las variables usadas estén definidas en el array de variables
   * @returns true si todas las variables están definidas, false si faltan
   */
  validateVariables(): boolean {
    const definedVariables = this.entity.get('variables')?.value || [];
    const usedVariables = this.extractAllVariables();
    
    // Verificar que todas las variables usadas estén en el array de variables definidas
    return usedVariables.every(variable => definedVariables.includes(variable));
  }

  /**
   * Limpia los templates opcionales que no tienen contenido
   * Esto es necesario antes de enviar al backend
   * @param formGroup - FormGroup del template
   * @returns Datos limpios sin templates vacíos
   */
  cleanTemplateData(formGroup: FormGroup): any {
    // Obtener el valor plano del FormGroup (sin referencias circulares)
    const cleanData = JSON.parse(JSON.stringify(formGroup.value));
    
    // Limpiar SMS template si está vacío
    if (!cleanData.smsTemplate?.message) {
      delete cleanData.smsTemplate;
    }
    
    // Limpiar WhatsApp template si está vacío
    if (!cleanData.whatsappTemplate?.message) {
      delete cleanData.whatsappTemplate;
    }
    
    // Limpiar Call script si está vacío
    if (!cleanData.callScript?.greeting && !cleanData.callScript?.mainMessage) {
      delete cleanData.callScript;
    }

    // Limpiar autoCallConfig si no tiene contenido útil
    if (cleanData.autoCallConfig) {
      const hasContent = 
        (cleanData.autoCallConfig.callType === 'TTS' && cleanData.autoCallConfig.ttsMessage) ||
        (cleanData.autoCallConfig.callType === 'AUDIO' && cleanData.autoCallConfig.audioUrl);
      
      if (!hasContent) {
        delete cleanData.autoCallConfig;
      }
    }
    
    return cleanData;
  }
}
